import { Helmet } from 'react-helmet-async'
import { Link, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import RenewalLayout from 'components/Renewal/RenewalLayout'
import { renewalPath } from 'components/Renewal/paths'
import { renewalRobots } from 'components/Renewal/seo'
import { useGetSuppliesQuery } from 'hooks/query/supplies/useGetSuppliesQuery'
import { SupplyProduct, supplyImagePath, supplyProductUrl } from 'lib/api/supplies/getSupplies'

const FACE = `Pretendard, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif`
const IMAGE_ROOT = 'https://image-cs.brwnie.kr/landing'

const SEO = {
  title: '무인매장 소모품 구매 — 청소용품·봉투·장갑 | 브라우니',
  description:
    '브라우니가 관리하는 무인매장에서 꼭 쓰는 소모품을 모았어요. 분리수거 봉투·니트릴 장갑·물걸레 청소포·물티슈를 쿠팡에서 바로 구매할 수 있어요.',
  canonical: 'https://brwnie.kr/shop/',
  siteName: '브라우니 무인매장 관리',
  image: 'https://brwnie.kr/assets/og/shop.jpg',
  imageAlt: '분리수거 배접 비닐봉투 60L 상품 사진',
}

const BREADCRUMB = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '홈', item: 'https://brwnie.kr/' },
    { '@type': 'ListItem', position: 2, name: '소모품 구매', item: 'https://brwnie.kr/shop/' },
  ],
}

function supplyImage(urlImage: string) {
  if (/^https?:\/\//i.test(urlImage)) return urlImage
  return `${IMAGE_ROOT}${urlImage}`
}

export default function RenewalShopPage() {
  const { pathname } = useLocation()
  const { data: products = [], isFetched } = useGetSuppliesQuery()

  return (
    <RenewalLayout current="shop" bandPage="shop">
      <Helmet>
        <title>{SEO.title}</title>
        <meta name="description" content={SEO.description} />
        <meta name="robots" content={renewalRobots(pathname)} />
        <link rel="canonical" href={SEO.canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={SEO.siteName} />
        <meta property="og:locale" content="ko_KR" />
        <meta property="og:title" content={SEO.title} />
        <meta property="og:description" content={SEO.description} />
        <meta property="og:url" content={SEO.canonical} />
        <meta property="og:image" content={SEO.image} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={SEO.imageAlt} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={SEO.title} />
        <meta name="twitter:description" content={SEO.description} />
        <meta name="twitter:image" content={SEO.image} />
        <link rel="alternate" type="application/rss+xml" title="브라우니 관리 사례·운영 가이드" href="https://brwnie.kr/rss.xml" />
        <script type="application/ld+json">{JSON.stringify(BREADCRUMB)}</script>
      </Helmet>
      <CrumbNav aria-label="breadcrumb">
        <CrumbInner>
          <CrumbList>
            <li>
              <CrumbHome to={renewalPath('/')}>홈</CrumbHome>
            </li>
            <CrumbSep aria-hidden="true">›</CrumbSep>
            <CrumbHere aria-current="page">소모품 구매</CrumbHere>
          </CrumbList>
        </CrumbInner>
      </CrumbNav>
      <Main data-screen-label="01 소모품 구매">
        <H1>
          <Eyebrow>무인매장 소모품 구매</Eyebrow>
          <Title>매장에 꼭 필요한 소모품, 바로 구매하세요</Title>
        </H1>
        <Lead>브라우니 크루가 관리하는 무인매장에서 꼭 쓰는 소모품 10가지를 모았어요. 쿠팡에서 바로 구매할 수 있어요.</Lead>
        <Section aria-labelledby="shop-list" data-shop-ready={isFetched ? 'true' : undefined}>
          <ListHead>
            <H2 id="shop-list">
              전체 상품
              <Count>{isFetched ? products.length : ''}</Count>
            </H2>
          </ListHead>
          <Grid>
            {products.map((product, index) => (
              <ProductCard key={`${product.url_product || product.name || 'item'}-${index}`} product={product} index={index} />
            ))}
          </Grid>
          <Note>이 페이지는 쿠팡 파트너스 활동의 일환으로, 이에 따른 일정액의 수수료를 제공받습니다.</Note>
        </Section>
      </Main>
    </RenewalLayout>
  )
}

function ProductCard({ product, index }: { product: SupplyProduct; index: number }) {
  const name = product.name || ''
  const imagePath = supplyImagePath(product)
  const image = imagePath ? supplyImage(imagePath) : ''
  const href = supplyProductUrl(product)

  const buyLabel = `${name}, 쿠팡에서 구매하러 가기 (새 창)`
  const buyLink = href
    ? { href, target: '_blank' as const, rel: 'sponsored noopener' }
    : null

  return (
    <Card>
      <Photo>
        {image && buyLink ? (
          <PhotoLink {...buyLink} data-cta={`shop_buy_${index + 1}`} aria-label={buyLabel}>
            <img src={image} alt="" width={800} height={800} loading="lazy" decoding="async" />
          </PhotoLink>
        ) : image ? (
          <img src={image} alt={name} width={800} height={800} loading="lazy" decoding="async" />
        ) : null}
      </Photo>
      <Badge>{product.tag || ''}</Badge>
      <Name>
        {buyLink ? (
          <NameLink {...buyLink} data-cta={`shop_buy_${index + 1}`} aria-label={buyLabel}>
            {name}
          </NameLink>
        ) : (
          name
        )}
      </Name>
      <BuyWrap>
        {buyLink ? (
          <Buy {...buyLink} data-cta={`shop_buy_${index + 1}`} aria-label={buyLabel}>
            구매하러 가기
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M7 17L17 7M9 7h8v8" />
            </svg>
          </Buy>
        ) : null}
      </BuyWrap>
    </Card>
  )
}

const CrumbNav = styled.nav`
  background: #ffffff;
  border-bottom: 1px solid #f2f4f6;
  font-family: ${FACE};
  word-break: keep-all;
`

const CrumbInner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 clamp(20px, 3.4vw, 40px);
  box-sizing: border-box;
  min-height: 44px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 16px;
  font-family: ${FACE};
  font-size: 14px;
  font-weight: 400;
  line-height: 1.6;
  color: #4e5968;
`

const CrumbList = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`

const CrumbHome = styled(Link)`
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 0 10px;
  margin: 0 -10px;
  font-family: ${FACE};
  font-size: 14px;
  font-weight: 400;
  line-height: 1.6;
  color: #4e5968;
  text-decoration: none;
`

const CrumbSep = styled.li`
  font-family: ${FACE};
  font-size: 14px;
  font-weight: 400;
  line-height: 1.6;
  color: #b0b8c1;
`

const CrumbHere = styled.li`
  font-family: ${FACE};
  font-size: 14px;
  font-weight: 700;
  line-height: 1.6;
  color: #191f28;
`

const Main = styled.main`
  max-width: 1280px;
  margin: 0 auto;
  padding: clamp(40px, 5vw, 72px) clamp(20px, 3.4vw, 40px) clamp(64px, 7vw, 104px);
  box-sizing: border-box;
`

const H1 = styled.h1`
  margin: 0;
  font-family: ${FACE};
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  color: #191f28;
`

const Eyebrow = styled.span`
  display: block;
  margin: 0 0 10px;
  font-family: ${FACE};
  font-size: 16px;
  font-weight: 700;
  line-height: 1.6;
  color: #3d5afe;
`

const Title = styled.span`
  display: block;
  font-family: ${FACE};
  font-size: clamp(30px, 3.6vw, 44px);
  font-weight: 800;
  line-height: 1.25;
  letter-spacing: -0.035em;
  color: #191f28;
  text-wrap: balance;
`

const Lead = styled.p`
  margin: 14px 0 0;
  max-width: 48ch;
  font-family: ${FACE};
  font-size: clamp(16px, 1.3vw, 18px);
  font-weight: 400;
  line-height: 1.7;
  color: #4e5968;
  text-wrap: pretty;
`

const Section = styled.section`
  margin-top: clamp(32px, 4vw, 48px);
`

const ListHead = styled.div`
  display: flex;
  align-items: center;
  min-height: 44px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e8eb;
`

const H2 = styled.h2`
  margin: 0;
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-family: ${FACE};
  font-size: 18px;
  font-weight: 800;
  line-height: 1.4;
  letter-spacing: -0.02em;
  color: #191f28;
`

const Count = styled.span`
  font-family: Gabarito, Pretendard, sans-serif;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.4;
  letter-spacing: -0.02em;
  color: #3d5afe;
`

const Grid = styled.ul`
  margin: 20px 0 0;
  padding: 0;
  list-style: none;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(max(150px, calc((100% - 64px) / 5)), 1fr));
  gap: clamp(28px, 3vw, 40px) 16px;
`

const Card = styled.li`
  min-width: 0;
  display: flex;
  flex-direction: column;
`

const Photo = styled.div`
  position: relative;
  aspect-ratio: 1 / 1;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid #e5e8eb;
  box-sizing: border-box;
  background: #ffffff;

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`

const PhotoLink = styled.a`
  position: absolute;
  inset: 0;
  display: block;
`

const Badge = styled.p`
  margin: 12px 0 0;
  font-family: ${FACE};
  font-size: 13px;
  font-weight: 700;
  line-height: 1.4;
  color: #3d5afe;
`

const Name = styled.h3`
  margin: 4px 0 0;
  min-height: 2.9em;
  font-family: ${FACE};
  font-size: 15px;
  font-weight: 600;
  line-height: 1.45;
  letter-spacing: -0.01em;
  color: #191f28;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const NameLink = styled.a`
  color: #191f28;
  font-family: ${FACE};
  font-size: 15px;
  font-weight: 600;
  line-height: 1.45;
  letter-spacing: -0.01em;
  text-decoration: none;

  &:hover {
    color: #191f28;
    text-decoration: none;
  }
`

const BuyWrap = styled.div`
  margin-top: auto;
  padding-top: 12px;
`

const Buy = styled.a`
  width: 100%;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border: 1px solid #d1d6db;
  border-radius: 10px;
  box-sizing: border-box;
  background: #ffffff;
  color: #003399;
  font-family: ${FACE};
  font-size: 15px;
  font-weight: 700;
  line-height: 1.6;
  text-decoration: none;

  &:hover {
    border-color: #003399;
    background: #f7f8fa;
    color: #003399;
  }
`

const Note = styled.p`
  margin: clamp(40px, 5vw, 56px) 0 0;
  font-family: ${FACE};
  font-size: 13px;
  font-weight: 400;
  line-height: 1.6;
  color: #6b7684;
`
